import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Eye, Download, Users, UserCheck, UserX, Trash2, Calendar, Tag, Target, DollarSign, Upload, Edit } from 'lucide-react';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import CampaignDownloadModal from '../components/campaigns/CampaignDownloadModal';
import DownloadFormatModal from '../components/campaigns/DownloadFormatModal';
import CampaignRemoveModal from '../components/campaigns/CampaignRemoveModal';
import CampaignUploadModal from '../components/campaigns/CampaignUploadModal';
import CampaignPopup from '../components/campaigns/CampaignPopup';
// toLimechatCSV, toEmailOnlyCSV, extractEmailsFromCSV moved to backend (CampaignService)
import CampaignAnalyticsCards from '../components/DashboardComponents/CampaignAnalyticsCards';
import Alert from '../components/common/Alert';
import { useAuth } from '../hooks/useAuth';
import { generateCampaignName, getEmptyCampaignForm } from '../utils/campaignNameGenerator';
import './CampaignDashboard.css';

const CampaignDashboard = () => {
  const { user } = useAuth();
  
  // Debug: Log user info (remove in production)
  useEffect(() => {
    console.log('CampaignDashboard - User object:', user);
    console.log('CampaignDashboard - User role:', user?.role);
  }, [user]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadType, setDownloadType] = useState('converted'); // 'converted' or 'non-converted'
  const [downloadCampaign, setDownloadCampaign] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [nonConvertedCount, setNonConvertedCount] = useState(0);
  const [convertedCount, setConvertedCount] = useState(0);
  const [removingKey, setRemovingKey] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [campaignToRemove, setCampaignToRemove] = useState(null);
  const [removingCampaign, setRemovingCampaign] = useState(false);
  const [removingEntire, setRemovingEntire] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [showCombinedAnalytics, setShowCombinedAnalytics] = useState(false);
  const [downloadingCombined, setDownloadingCombined] = useState(false);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [campaignForm, setCampaignForm] = useState(() => getEmptyCampaignForm());
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showCampaignUploadModal, setShowCampaignUploadModal] = useState(false);
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [updatingCampaign, setUpdatingCampaign] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [performanceInfo, setPerformanceInfo] = useState(null);
  const [dataLastUpdatedAt, setDataLastUpdatedAt] = useState(null);
  const [nextUpdateAt, setNextUpdateAt] = useState(() => getNextScheduledUpdate());
  const [loadingCountKey, setLoadingCountKey] = useState(null); // 'campaignName-source-converted' | 'campaignName-source-non-converted'
  const [downloadingCampaignKey, setDownloadingCampaignKey] = useState(null); // loader on counter box during download/save+download
  const activePollingRef = useRef(new Map()); // jobId -> intervalId — cleaned up on unmount
  const [showDownloadFormatModal, setShowDownloadFormatModal] = useState(false);
  const [isManuallyEditedCampaignName, setIsManuallyEditedCampaignName] = useState(false);
  // Dropdown: select campaigns from all pages for combined performance
  const [campaignList, setCampaignList] = useState([]);
  const [dropdownSelectedCampaigns, setDropdownSelectedCampaigns] = useState([]);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [dropdownSearchQuery, setDropdownSearchQuery] = useState('');
  const [loadingCampaignList, setLoadingCampaignList] = useState(false);
  const [loadingCombinedPerformance, setLoadingCombinedPerformance] = useState(false);
  const [queueAlertMessage, setQueueAlertMessage] = useState(null); // "Download queued" / "Saving campaign & preparing..." — shown in custom Alert
  /** When set, table shows only these rows (e.g. after "View combined performance" from dropdown). */
  const [combinedTableCampaigns, setCombinedTableCampaigns] = useState(null);
  const [rangeTotalSpend, setRangeTotalSpend] = useState(null);

  const ITEMS_PER_PAGE = 10;

  // Clean up all polling intervals when the component unmounts (declared early so
  // it can be referenced before the effect below)
  // NOTE: activePollingRef cleanup is in its own effect further down.

  // Returns the next :00/:15/:30/:45 clock boundary strictly after now (scheduler runs every 15 min).
  // Math.floor + interval avoids the Math.ceil edge-case where a boundary-exact time returns itself.
  function getNextScheduledUpdate() {
    const now = Date.now();
    const interval = 15 * 60 * 1000;
    return new Date(Math.floor(now / interval) * interval + interval);
  }

  const tableCampaigns = React.useMemo(
    () => combinedTableCampaigns ?? campaigns,
    [combinedTableCampaigns, campaigns]
  );

  // Fetch campaigns with performance data
  // skipCache: when true, backend clears campaign cache and returns fresh data (for Refresh / after Remove / after Upload)
  const fetchCampaigns = useCallback(async (page = 1, dateFromParam = null, dateToParam = null, skipCache = false) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    setLoadingProgress(0);

    let progressInterval = null;
    try {
      console.log(`🚀 Fetching campaigns page ${page}${skipCache ? ' (skip cache)' : ''}...`);
      
      // Simulate loading progress
      progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 500);
      
      const params = {
        page,
        limit: ITEMS_PER_PAGE
      };
      
      // Add date filters if provided
      const fromDate = dateFromParam !== null ? dateFromParam : dateFrom;
      const toDate = dateToParam !== null ? dateToParam : dateTo;
      
      if (fromDate) {
        params.dateFrom = fromDate;
      }
      if (toDate) {
        params.dateTo = toDate;
      }
      
      if (skipCache) {
        params.skipCache = 'true';
      }
      
      const response = await axios.get('/campaign/campaigns', {
        params,
        timeout: 25000 // 25 second timeout - increased for complex queries
      });

      clearInterval(progressInterval);
      setLoadingProgress(100);

      if (response.data.success) {
        const newCampaigns = response.data.campaigns || [];
        
        // Always replace campaigns (no appending for pagination)
        setCampaigns(newCampaigns);

        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalCampaigns(response.data.totalCampaigns || 0);
        setHasMore(response.data.pagination?.hasMore || false);
        if (fromDate || toDate) {
          setRangeTotalSpend(Number(response.data.rangeTotalSpend || 0));
        } else {
          setRangeTotalSpend(null);
        }
        
        // Store performance info
        setPerformanceInfo(response.data.performance);

        // Derive the most recent lastComputedAt across the current page
        const maxComputedAt = newCampaigns.reduce((max, c) => {
          const t = c._lastComputedAt ? new Date(c._lastComputedAt).getTime() : 0;
          return t > max ? t : max;
        }, 0);
        if (maxComputedAt > 0) setDataLastUpdatedAt(new Date(maxComputedAt));
        setNextUpdateAt(getNextScheduledUpdate());
        
        // Auto-calculate combined analytics if date filter is applied
        if (newCampaigns.length > 0 && (dateFrom || dateTo)) {
          const totalCustomers = newCampaigns.reduce((sum, c) => sum + (c.totalCustomers || 0), 0);
          const totalOrders = newCampaigns.reduce((sum, c) => sum + (c.totalOrders || 0), 0);
          const totalGMV = newCampaigns.reduce((sum, c) => sum + (c.totalGMV || 0), 0);
          const totalSpend = newCampaigns.reduce((sum, c) => sum + (c.campaignSpend || 0), 0);
          const totalLogins = newCampaigns.reduce((sum, c) => sum + (c.totalLogins || 0), 0);
          const customersWithOrders = newCampaigns.reduce((sum, c) => sum + (c.customersWithOrders || 0), 0);
          const newCustomersAcquired = newCampaigns.reduce((sum, c) => sum + (c.newCustomersAcquired ?? 0), 0);
          const costPerActivity = totalLogins > 0 ? (totalSpend / totalLogins) : null;
          const avgOrderValue = totalOrders > 0 ? (totalGMV / totalOrders) : null;
          const costPerOrder = totalOrders > 0 ? (totalSpend / totalOrders) : null;
          const customerAcquisitionCost = newCustomersAcquired > 0 ? (totalSpend / newCustomersAcquired) : null;
          
          setPerformance({
            totalCampaigns: newCampaigns.length,
            totalCustomers,
            customersWithOrders,
            totalOrders,
            totalGMV,
            totalSpend,
            totalLogins,
            avgConversionRate: totalCustomers > 0 ? ((customersWithOrders / totalCustomers) * 100) : 0,
            overallROI: totalSpend > 0 ? (totalGMV / totalSpend) : 0,
            costPerActivity,
            avgOrderValue,
            costPerOrder,
            newCustomersAcquired,
            customerAcquisitionCost
          });
          
          setCombinedTableCampaigns(null);
          setShowCombinedAnalytics(true);
        } else if (!dateFrom && !dateTo) {
          // Clear analytics if no date filter
          setPerformance(null);
          setShowCombinedAnalytics(false);
          setCombinedTableCampaigns(null);
          setRangeTotalSpend(null);
        }

        console.log(`✅ Loaded ${newCampaigns.length} campaigns (page ${page})`);
      } else {
        setError('Failed to fetch campaigns');
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. The server might be overloaded. Try using date filters to reduce the data load.');
      } else if (err.response?.status === 408) {
        setError(err.response?.data?.message || 'Request timed out. Try filtering by date range to improve performance.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to fetch campaigns');
      }
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setLoading(false);
      setLoadingProgress(0);
    }
  }, [loading, dateFrom, dateTo]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !loading) {
      setCurrentPage(newPage);
      setSelectedCampaigns(new Set()); // Clear selections when changing pages
      setShowCombinedAnalytics(false); // Reset analytics view when changing pages
      fetchCampaigns(newPage);
    }
  }, [currentPage, totalPages, loading, fetchCampaigns]);

  // Refresh campaigns (clears server cache and fetches fresh data)
  const refreshCampaigns = useCallback(() => {
    setCurrentPage(1);
    setCampaigns([]);
    setSelectedCampaigns(new Set());
    setShowCombinedAnalytics(false);
    setCampaignList([]); // so dropdown refetches list on next open
    fetchCampaigns(1, null, null, true); // skipCache: true so backend clears cache and returns fresh data
  }, [fetchCampaigns]);

  // Initial load
  useEffect(() => {
    fetchCampaigns(1);
  }, []);

  // Clean up all polling intervals when the component unmounts
  useEffect(() => {
    return () => {
      for (const intervalId of activePollingRef.current.values()) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Recompute next scheduled update time every 30 seconds so it stays accurate
  useEffect(() => {
    const id = setInterval(() => setNextUpdateAt(getNextScheduledUpdate()), 30000);
    return () => clearInterval(id);
  }, []);

  // Close campaign dropdown when clicking outside
  const campaignDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCampaignDropdown && campaignDropdownRef.current && !campaignDropdownRef.current.contains(event.target)) {
        setShowCampaignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCampaignDropdown]);

  // Format currency (allow decimals so e.g. cost 2.5 per message shows as ₹2.5, not ₹3)
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value || value === 0) return '0%';
    return `${value.toFixed(1)}%`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handle view details
  const handleViewDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedCampaign(null);
  };

  // Generic file downloader (shows in Network tab)
  const downloadFile = async (url, filename, onFinally = () => {}) => {
    try {
      const response = await axios.get(url, {
        responseType: 'blob',
        timeout: 20000
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      const msg = error.response?.data?.message || error.message || 'Download failed';
      toast.error(msg);
    } finally {
      onFinally();
    }
  };

  /**
   * Poll a background job until it is done or errored.
   * When done, downloads the result CSV via axios (so the dev proxy works).
   * @param {string} jobId
   * @param {string} downloadKey - key used to show/hide the counter-box loader
   * @param {boolean} didSaveCampaign - if true, refresh the campaign list after download
   */
  const pollJobStatus = useCallback((jobId, downloadKey, didSaveCampaign = false) => {
    const intervalId = setInterval(async () => {
      try {
        const resp = await axios.get(`/campaign/job-status/${jobId}`);
        const { status, filename, error: jobError, customerCount } = resp.data;

        if (status === 'done') {
          clearInterval(intervalId);
          activePollingRef.current.delete(jobId);

          // Download the result via axios so the dev proxy is used
          const downloadResp = await axios.get(`/campaign/job-result/${jobId}`, { responseType: 'blob' });
          const blob = new Blob([downloadResp.data], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);

          toast.success(`Download complete! (${(customerCount || 0).toLocaleString()} customers)`);
          setDownloading(false);
          setSavingCampaign(false);
          setDownloadingCampaignKey(null);
          setQueueAlertMessage(null);

          if (didSaveCampaign) {
            refreshCampaigns();
          }
        } else if (status === 'error') {
          clearInterval(intervalId);
          activePollingRef.current.delete(jobId);
          toast.error(`Download failed: ${jobError || 'Unknown error'}`);
          setDownloading(false);
          setSavingCampaign(false);
          setDownloadingCampaignKey(null);
          setQueueAlertMessage(null);
        }
        // else: pending | running — keep polling
      } catch (err) {
        clearInterval(intervalId);
        activePollingRef.current.delete(jobId);
        toast.error('Lost connection to download job. Please try again.');
        setDownloading(false);
        setSavingCampaign(false);
        setDownloadingCampaignKey(null);
        setQueueAlertMessage(null);
      }
    }, 3000); // poll every 3 seconds

    activePollingRef.current.set(jobId, intervalId);
  }, [refreshCampaigns]);

  // Open modal immediately, then load count in the background (no blocking)
  const handleDownloadConverted = (campaign) => {
    const key = `${campaign.campaignName}-${campaign.source}-converted`;
    setConvertedCount(null); // null = still loading
    setDownloadCampaign(campaign);
    setDownloadType('converted');
    setShowDownloadModal(true);

    // Load count async — does NOT block modal from opening
    setLoadingCountKey(key);
    const sourceParam = (campaign.source || '').toLowerCase();
    axios.get(`/campaign/converted-customers-count/${encodeURIComponent(campaign.campaignName)}/${encodeURIComponent(sourceParam)}`, { timeout: 25000 })
      .then(response => {
        if (response.data.success) setConvertedCount(response.data.convertedCount);
      })
      .catch(err => {
        console.error('Error loading converted count:', err);
        setConvertedCount(-1); // -1 = failed to load
      })
      .finally(() => setLoadingCountKey(null));
  };

  // Open modal immediately, then load count in the background (no blocking)
  const handleDownloadNonConverted = (campaign) => {
    const key = `${campaign.campaignName}-${campaign.source}-non-converted`;
    setNonConvertedCount(null); // null = still loading
    setDownloadCampaign(campaign);
    setDownloadType('non-converted');
    setShowDownloadModal(true);

    // Load count async — does NOT block modal from opening
    setLoadingCountKey(key);
    const sourceParam = (campaign.source || '').toLowerCase();
    axios.get(`/campaign/non-converted-customers-count/${encodeURIComponent(campaign.campaignName)}/${encodeURIComponent(sourceParam)}`, { timeout: 25000 })
      .then(response => {
        if (response.data.success) setNonConvertedCount(response.data.nonConvertedCount);
      })
      .catch(err => {
        console.error('Error loading non-converted count:', err);
        setNonConvertedCount(-1); // -1 = failed to load
      })
      .finally(() => setLoadingCountKey(null));
  };

  // Start a background download job — modal closes, loader on counter box, no page hang
  const handleDownloadWithoutCampaign = () => {
    if (!downloadCampaign) return;

    const key = `${downloadCampaign.campaignName}-${downloadCampaign.source}-${downloadType}`;
    setDownloading(true);
    setDownloadingCampaignKey(key);
    setShowDownloadModal(false);

    const sourceParam = (downloadCampaign.source || '').toLowerCase();
    axios.post('/campaign/start-download-job', {
      campaignName: downloadCampaign.campaignName,
      source: sourceParam,
      downloadType,
      withCampaign: false,
      format: 'default'
    })
      .then(response => {
        if (response.data.success) {
          const msg = 'Download queued — file will save automatically when ready.';
          setQueueAlertMessage(msg);
          toast(msg, { icon: '⏳' });
          pollJobStatus(response.data.jobId, key, false);
        } else {
          toast.error('Failed to start download');
          setDownloading(false);
          setDownloadingCampaignKey(null);
        }
      })
      .catch(err => {
        toast.error(err.response?.data?.message || 'Failed to start download');
        setDownloading(false);
        setDownloadingCampaignKey(null);
      });
  };

  // Update downloadFile to support POST requests
  const downloadFilePost = async (url, data, filename, onFinally = () => {}) => {
    try {
      const response = await axios.post(url, data, {
        responseType: 'blob',
        timeout: 20000
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      const msg = error.response?.data?.message || error.message || 'Download failed';
      toast.error(msg);
    } finally {
      onFinally();
    }
  };

  // Campaign form handling functions
  const handleCampaignFormChange = useCallback((field, value, isEditMode = false) => {
    setCampaignForm(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If user is directly editing the campaign name field, mark it as manually edited
      if (field === 'campaignName') {
        setIsManuallyEditedCampaignName(true);
        return updated;
      }
      
      // Auto-generate campaign name when source, objective, campaignDate, or coupon changes
      // Only auto-generate in create mode, not edit mode
      // Also check if the user has manually edited the campaign name - if so, don't auto-generate
      if (!isEditMode && (field === 'source' || field === 'objective' || field === 'campaignDate' || field === 'coupon')) {
        // Don't auto-generate if user has manually edited the campaign name
        if (!isManuallyEditedCampaignName) {
          const generatedName = generateCampaignName({
            campaignDate: field === 'campaignDate' ? value : updated.campaignDate,
            source: field === 'source' ? value : updated.source,
            objective: field === 'objective' ? value : updated.objective,
            coupon: field === 'coupon' ? value : updated.coupon
          });
          
          // Only update if we have enough data to generate a name
          if (generatedName) {
            updated.campaignName = generatedName;
          }
        }
      }
      
      return updated;
    });
  }, [isManuallyEditedCampaignName]);

  const handleOfferChange = useCallback((offer) => {
    setCampaignForm(prev => ({
      ...prev,
      offers: prev.offers.includes(offer)
        ? prev.offers.filter(o => o !== offer)
        : [...prev.offers, offer]
    }));
  }, []);

  // Handle opening campaign popup from download modal
  const handleOpenCampaignPopup = () => {
    setShowDownloadModal(false);
    setShowCampaignPopup(true);
    setIsManuallyEditedCampaignName(false); // Reset manual edit flag for new campaign
    setCampaignForm(getEmptyCampaignForm());
  };

  // Handle download with campaign (save campaign info and download)
  const handleDownloadWithCampaign = async () => {
    // This will be called from CampaignPopup after form is filled
    setShowCampaignPopup(false);
  };

  // Handle save campaign and download (called from CampaignPopup) - opens format choice modal
  const handleSaveCampaignAndDownload = () => {
    if (!downloadCampaign) return;

    if (!campaignForm.campaignName || !campaignForm.source ||
        !campaignForm.pricePerMessage || !campaignForm.campaignDate ||
        !campaignForm.objective) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (campaignForm.source === 'others' && !campaignForm.sourceRemarks) {
      toast.error('Please provide remarks for "others" source');
      return;
    }

    if (campaignForm.offers.includes('Other') && !campaignForm.otherOfferDetails) {
      toast.error('Please provide details for "Other" offer');
      return;
    }

    setShowDownloadFormatModal(true);
  };

  // Start a background job that saves campaign info + generates CSV — no page hang
  const handleDownloadFormatSelected = (format) => {
    if (!downloadCampaign) return;

    // Snapshot form values before resetting state
    const campaignFormSnapshot = { ...campaignForm };
    const downloadKey = `${downloadCampaign.campaignName}-${downloadCampaign.source}-${downloadType}`;
    setSavingCampaign(true);
    setDownloading(true);
    setDownloadingCampaignKey(downloadKey);

    // Close all modals immediately so the page doesn't hang
    setShowDownloadFormatModal(false);
    setShowDownloadModal(false);
    setShowCampaignPopup(false);
    setIsManuallyEditedCampaignName(false);
    setCampaignForm(getEmptyCampaignForm());

    const sourceParam = (downloadCampaign.source || '').toLowerCase();
    axios.post('/campaign/start-download-job', {
      campaignName: downloadCampaign.campaignName,
      source: sourceParam,
      downloadType,
      withCampaign: true,
      campaignForm: campaignFormSnapshot,
      format
    })
      .then(response => {
        if (response.data.success) {
          const msg = 'Saving campaign & preparing download — file will save automatically when ready.';
          setQueueAlertMessage(msg);
          toast(msg, { icon: '⏳' });
          pollJobStatus(response.data.jobId, downloadKey, true);
        } else {
          toast.error('Failed to start download job');
          setDownloading(false);
          setSavingCampaign(false);
          setDownloadingCampaignKey(null);
        }
      })
      .catch(err => {
        toast.error(err.response?.data?.message || 'Failed to start download job');
        setDownloading(false);
        setSavingCampaign(false);
        setDownloadingCampaignKey(null);
      });
  };

  // Handle remove campaign button click - show remove modal
  const handleRemoveCampaign = (campaign) => {
    setCampaignToRemove(campaign);
    setShowRemoveModal(true);
  };

  // Handle remove entire campaign (admin only)
  const handleRemoveEntireCampaign = async () => {
    if (!campaignToRemove) return;

    setRemovingEntire(true);
    const sourceParam = (campaignToRemove.source || '').toLowerCase();

    try {
      const response = await axios.delete(
        `/campaign/remove-entire-campaign/${encodeURIComponent(campaignToRemove.campaignName)}/${encodeURIComponent(sourceParam)}`
      );

      if (response.data.success) {
        const modifiedCount = response.data.modifiedCount || 0;
        toast.success(
          `Removed campaign "${campaignToRemove.campaignName}" from ${modifiedCount} customer(s)`
        );
        setShowRemoveModal(false);
        setCampaignToRemove(null);
        refreshCampaigns();
      } else {
        toast.error(response.data.message || 'Failed to remove campaign');
      }
    } catch (error) {
      console.error('Error removing entire campaign:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to remove campaign';
      toast.error(msg);
    } finally {
      setRemovingEntire(false);
    }
  };

  // Handle file upload for campaign removal
  const handleFileUpload = async (file) => {
    if (!campaignToRemove) return;

    setRemovingCampaign(true);
    const sourceParam = (campaignToRemove.source || '').toLowerCase();
    const key = `${campaignToRemove.campaignName}-${campaignToRemove.source}`;
    setRemovingKey(key);

    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // Parse CSV to extract emails or phone numbers
      const lines = fileContent.split('\n');
      const identifiers = []; // Will contain emails or phone numbers
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,15}$/;
      
      // Process all rows (including header - we'll filter out invalid identifiers)
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Handle CSV with quoted fields
        const columns = [];
        let currentCol = '';
        let inQuotes = false;
        
        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            columns.push(currentCol.trim());
            currentCol = '';
          } else {
            currentCol += char;
          }
        }
        columns.push(currentCol.trim()); // Add last column
        
        // Look for email or phone number in any column
        let found = false;
        for (const col of columns) {
          const cleanCol = col.replace(/^"|"$/g, '').trim();
          const cleanColLower = cleanCol.toLowerCase();
          
          // Check if it's an email
          if (emailRegex.test(cleanColLower)) {
            identifiers.push(cleanColLower);
            found = true;
            break; // Only take first identifier per row
          }
          
          // Check if it's a phone number
          if (phoneRegex.test(cleanCol)) {
            identifiers.push(cleanCol);
            found = true;
            break; // Only take first identifier per row
          }
        }
      }

      if (identifiers.length === 0) {
        toast.error('No valid email addresses or phone numbers found in the file');
        setRemovingCampaign(false);
        setRemovingKey(null);
        return;
      }

      // Send identifiers (emails or phone numbers) to backend for selective removal
      const response = await axios.post(
        `/campaign/remove-by-emails/${encodeURIComponent(campaignToRemove.campaignName)}/${encodeURIComponent(sourceParam)}`,
        { identifiers }
      );

      if (response.data.success) {
        toast.success(
          `Removed campaign "${campaignToRemove.campaignName}" from ${response.data.removedCount} customers`
        );
        setShowRemoveModal(false);
        setCampaignToRemove(null);
      refreshCampaigns();
      } else {
        toast.error(response.data.message || 'Failed to remove campaign');
      }
    } catch (error) {
      console.error('Error removing campaign:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to remove campaign';
      toast.error(msg);
    } finally {
      setRemovingCampaign(false);
      setRemovingKey(null);
    }
  };

  // Close remove modal
  const closeRemoveModal = () => {
    setShowRemoveModal(false);
    setCampaignToRemove(null);
  };

  // Close download modal
  const closeDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadCampaign(null);
    setDownloadType('converted');
    setNonConvertedCount(null);
    setConvertedCount(null);
  };

  // Handle checkbox selection
  const handleCampaignSelect = (campaign) => {
    const key = `${campaign.campaignName}-${campaign.source}`;
    setSelectedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Handle select all / deselect all (respects visible table rows)
  const handleSelectAll = () => {
    const rows = combinedTableCampaigns ?? campaigns;
    if (selectedCampaigns.size === rows.length && rows.length > 0) {
      setSelectedCampaigns(new Set());
    } else {
      const allKeys = rows.map(c => `${c.campaignName}-${c.source}`);
      setSelectedCampaigns(new Set(allKeys));
    }
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedCampaigns(new Set());
    if (showCombinedAnalytics) {
      handleResetAnalytics();
    }
  };

  // Get selected campaign objects (current table rows — paginated or combined subset)
  const getSelectedCampaignObjects = () => {
    const rows = combinedTableCampaigns ?? campaigns;
    return rows.filter(c => selectedCampaigns.has(`${c.campaignName}-${c.source}`));
  };

  // Handle combined download (converted)
  const handleDownloadCombinedConverted = async () => {
    const selected = getSelectedCampaignObjects();
    if (selected.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }

    setDownloadingCombined(true);
    try {
      const campaignsData = selected.map(c => ({
        campaignName: c.campaignName,
        source: c.source
      }));

      const url = '/campaign/download-combined-converted-customers';
      const filename = `combined-converted-customers-${Date.now()}.csv`;
      
      await downloadFilePost(
        url,
        { campaigns: campaignsData },
        filename,
        () => setDownloadingCombined(false)
      );
    } catch (error) {
      console.error('Error downloading combined converted customers:', error);
      toast.error('Failed to download combined converted customers');
      setDownloadingCombined(false);
    }
  };

  // Handle combined download (non-converted)
  const handleDownloadCombinedNonConverted = async () => {
    const selected = getSelectedCampaignObjects();
    if (selected.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }

    setDownloadingCombined(true);
    try {
      const campaignsData = selected.map(c => ({
        campaignName: c.campaignName,
        source: c.source
      }));

      const url = '/campaign/download-combined-non-converted-customers';
      const filename = `combined-non-converted-customers-${Date.now()}.csv`;
      
      await downloadFilePost(
        url,
        { campaigns: campaignsData },
        filename,
        () => setDownloadingCombined(false)
      );
    } catch (error) {
      console.error('Error downloading combined non-converted customers:', error);
      toast.error('Failed to download combined non-converted customers');
      setDownloadingCombined(false);
    }
  };

  // Handle view combined analytics
  const handleViewCombinedAnalytics = () => {
    const selected = getSelectedCampaignObjects();
    if (selected.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }

    const totalCustomers = selected.reduce((sum, c) => sum + (c.totalCustomers || 0), 0);
    const totalOrders = selected.reduce((sum, c) => sum + (c.totalOrders || 0), 0);
    const totalGMV = selected.reduce((sum, c) => sum + (c.totalGMV || 0), 0);
    const totalSpend = selected.reduce((sum, c) => sum + (c.campaignSpend || 0), 0);
    const totalLogins = selected.reduce((sum, c) => sum + (c.totalLogins || 0), 0);
    const customersWithOrders = selected.reduce((sum, c) => sum + (c.customersWithOrders || 0), 0);
    const newCustomersAcquired = selected.reduce((sum, c) => sum + (c.newCustomersAcquired ?? 0), 0);
    const costPerActivity = totalLogins > 0 ? (totalSpend / totalLogins) : null;
    const avgOrderValue = totalOrders > 0 ? (totalGMV / totalOrders) : null;
    const costPerOrder = totalOrders > 0 ? (totalSpend / totalOrders) : null;
    const customerAcquisitionCost = newCustomersAcquired > 0 ? (totalSpend / newCustomersAcquired) : null;

    setPerformance({
      totalCampaigns: selected.length,
      totalCustomers,
      customersWithOrders,
      totalOrders,
      totalGMV,
      totalSpend,
      totalLogins,
      avgConversionRate: totalCustomers > 0 ? ((customersWithOrders / totalCustomers) * 100) : 0,
      overallROI: totalSpend > 0 ? (totalGMV / totalSpend) : 0,
      costPerActivity,
      avgOrderValue,
      costPerOrder,
      newCustomersAcquired,
      customerAcquisitionCost
    });

    setCombinedTableCampaigns(selected);
    setShowCombinedAnalytics(true);
    toast.success(`Showing analytics for ${selected.length} selected campaign(s)`);
  };

  // Fetch campaign list for dropdown (all pages)
  const fetchCampaignList = useCallback(async () => {
    setLoadingCampaignList(true);
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await axios.get('/campaign/list', { params, timeout: 15000 });
      if (response.data.success) {
        setCampaignList(response.data.campaigns || []);
      }
    } catch (err) {
      console.error('Error fetching campaign list:', err);
      toast.error(err.response?.data?.message || 'Failed to load campaign list');
      setCampaignList([]);
    } finally {
      setLoadingCampaignList(false);
    }
  }, [dateFrom, dateTo]);

  // Open dropdown and fetch list if needed
  const handleCampaignDropdownToggle = (e) => {
    if (e && e.target.closest && e.target.closest('input')) return;
    const next = !showCampaignDropdown;
    setShowCampaignDropdown(next);
    if (next && campaignList.length === 0) {
      fetchCampaignList();
    } else if (next && (dateFrom || dateTo)) {
      fetchCampaignList();
    }
  };

  const handleCampaignFieldFocus = () => {
    if (!showCampaignDropdown) setShowCampaignDropdown(true);
    if (campaignList.length === 0) fetchCampaignList();
    else if (dateFrom || dateTo) fetchCampaignList();
  };

  const dropdownCampaignKey = (c) => `${c.campaignName}-${c.source}`;
  const isDropdownCampaignSelected = (c) =>
    dropdownSelectedCampaigns.some(
      (s) => s.campaignName === c.campaignName && (s.source || '').toLowerCase() === (c.source || '').toLowerCase()
    );
  const handleDropdownCampaignToggle = (c) => {
    setDropdownSelectedCampaigns((prev) => {
      if (isDropdownCampaignSelected(c)) {
        return prev.filter(
          (s) => !(s.campaignName === c.campaignName && (s.source || '').toLowerCase() === (c.source || '').toLowerCase())
        );
      }
      return [...prev, { campaignName: c.campaignName, source: c.source }];
    });
  };
  // Filtered list for dropdown (by search query)
  const filteredCampaignList = React.useMemo(() => {
    const q = (dropdownSearchQuery || '').trim().toLowerCase();
    if (!q) return campaignList;
    return campaignList.filter((c) => {
      const name = (c.campaignName || '').toLowerCase();
      const source = (c.source || '').toLowerCase();
      return name.includes(q) || source.includes(q);
    });
  }, [campaignList, dropdownSearchQuery]);

  const handleDropdownSelectAll = () => {
    const filtered = filteredCampaignList;
    const allFilteredSelected =
      filtered.length > 0 && filtered.every((c) => isDropdownCampaignSelected(c));
    if (allFilteredSelected) {
      setDropdownSelectedCampaigns((prev) => {
        const toRemove = new Set(
          filtered.map((c) => `${c.campaignName}-${(c.source || '').toLowerCase()}`)
        );
        return prev.filter(
          (s) => !toRemove.has(`${s.campaignName}-${(s.source || '').toLowerCase()}`)
        );
      });
    } else {
      setDropdownSelectedCampaigns((prev) => {
        const existing = new Set(
          prev.map((s) => `${s.campaignName}-${(s.source || '').toLowerCase()}`)
        );
        const toAdd = filtered
          .filter((c) => !existing.has(`${c.campaignName}-${(c.source || '').toLowerCase()}`))
          .map((c) => ({ campaignName: c.campaignName, source: c.source }));
        return [...prev, ...toAdd];
      });
    }
  };
  const handleDropdownClear = () => {
    setDropdownSelectedCampaigns([]);
  };

  // View combined performance from dropdown (campaigns from all pages)
  const handleViewCombinedAnalyticsFromDropdown = async () => {
    if (dropdownSelectedCampaigns.length === 0) {
      toast.error('Please select at least one campaign from the dropdown');
      return;
    }
    setLoadingCombinedPerformance(true);
    try {
      const response = await axios.post('/campaign/combined-performance', {
        campaigns: dropdownSelectedCampaigns,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      });
      if (response.data.success && response.data.performance) {
        setPerformance(response.data.performance);
        setCombinedTableCampaigns(
          Array.isArray(response.data.campaigns) ? response.data.campaigns : []
        );
        setShowCombinedAnalytics(true);
        toast.success(`Showing combined analytics for ${dropdownSelectedCampaigns.length} campaign(s)`);
      } else {
        toast.error('Failed to load combined performance');
      }
    } catch (err) {
      console.error('Error fetching combined performance:', err);
      toast.error(err.response?.data?.message || 'Failed to load combined performance');
    } finally {
      setLoadingCombinedPerformance(false);
    }
  };

  // Handle reset analytics view
  const handleResetAnalytics = () => {
    setShowCombinedAnalytics(false);
    setPerformance(null);
    setCombinedTableCampaigns(null);
    setDropdownSelectedCampaigns([]);
    setShowCampaignDropdown(false);
    setRangeTotalSpend(null);
    if (dateFrom || dateTo) {
      setDateFrom('');
      setDateTo('');
      fetchCampaigns(1, '', '');
    }
    toast.success('Analytics reset');
  };

  // Check if campaign can be edited (within 7 days of creation)
  const canEditCampaign = (campaign) => {
    if (!campaign.createdAt) return false;
    const createdAt = new Date(campaign.createdAt);
    const now = new Date();
    const daysDiff = (now - createdAt) / (1000 * 60 * 60 * 24);
    return daysDiff <= 7;
  };

  // Handle edit campaign
  const handleEditCampaign = (campaign) => {
    if (!canEditCampaign(campaign)) {
      toast.error('Campaign can only be edited within 7 days of creation');
      return;
    }

    // Format campaign date for input (YYYY-MM-DD)
    const campaignDate = campaign.campaignDate 
      ? new Date(campaign.campaignDate).toISOString().split('T')[0]
      : '';

    setEditingCampaign(campaign);
    setIsManuallyEditedCampaignName(true); // In edit mode, consider existing name as manually edited
    setCampaignForm({
      campaignName: campaign.campaignName || '',
      source: campaign.source || '',
      sourceRemarks: campaign.sourceRemarks || '',
      pricePerMessage: campaign.pricePerMessage?.toString() || '',
      campaignDate: campaignDate,
      objective: campaign.objective || '',
      offers: Array.isArray(campaign.offers) ? campaign.offers : [],
      otherOfferDetails: campaign.otherOfferDetails || '',
      coupon: campaign.coupon || ''
    });
    setShowEditCampaignModal(true);
  };

  // Handle update campaign
  const handleUpdateCampaign = async () => {
    if (!editingCampaign) return;

    // Validate required fields
    if (!campaignForm.campaignName || !campaignForm.source || 
        !campaignForm.pricePerMessage || !campaignForm.campaignDate || 
        !campaignForm.objective) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (campaignForm.source === 'others' && !campaignForm.sourceRemarks) {
      toast.error('Please provide remarks for "others" source');
      return;
    }

    if (campaignForm.offers.includes('Other') && !campaignForm.otherOfferDetails) {
      toast.error('Please provide details for "Other" offer');
      return;
    }

    setUpdatingCampaign(true);

    try {
      const response = await axios.put(
        `/campaign/update-campaign/${encodeURIComponent(editingCampaign.campaignName)}/${encodeURIComponent(editingCampaign.source)}`,
        {
          campaignName: campaignForm.campaignName,
          source: campaignForm.source,
          sourceRemarks: campaignForm.source === 'others' ? campaignForm.sourceRemarks : undefined,
          pricePerMessage: parseFloat(campaignForm.pricePerMessage),
          campaignDate: campaignForm.campaignDate,
          objective: campaignForm.objective,
          offers: campaignForm.offers,
          otherOfferDetails: campaignForm.offers.includes('Other') ? campaignForm.otherOfferDetails : undefined,
          coupon: campaignForm.coupon || undefined
        }
      );

      if (response.data.success) {
        toast.success('Campaign updated successfully');
        setShowEditCampaignModal(false);
        setEditingCampaign(null);
        refreshCampaigns();
      } else {
        toast.error(response.data.message || 'Failed to update campaign');
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to update campaign');
    } finally {
      setUpdatingCampaign(false);
    }
  };

  // Close edit campaign modal
  const closeEditCampaignModal = () => {
    setShowEditCampaignModal(false);
    setEditingCampaign(null);
    setIsManuallyEditedCampaignName(false); // Reset manual edit flag
    setCampaignForm(getEmptyCampaignForm());
  };

  // Handle apply date filter
  const handleApplyDateFilter = () => {
    // Validate date range
    if (dateFrom && dateTo && new Date(dateTo) < new Date(dateFrom)) {
      toast.error('To date must be after From date');
      return;
    }
    
    setCurrentPage(1); // Reset to first page
    setSelectedCampaigns(new Set()); // Clear selections
    fetchCampaigns(1);
  };

  // Handle clear date filter
  const handleClearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
    setSelectedCampaigns(new Set());
    setDropdownSelectedCampaigns([]);
    setCampaignList([]);
    setShowCombinedAnalytics(false);
    setPerformance(null);
    fetchCampaigns(1, '', '');
  };

  return (
    <motion.div
      className="campaign-dashboard-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header
        title="Campaign Dashboard"
        subtitle="Monitor campaign performance and attribution"
        showBackButton={true}
        backPath="/menu"
        lastUpdated={dataLastUpdatedAt || new Date()}
      />

      <div className="campaign-content">
        {/* Download queued / in progress — shown in custom Alert */}
        {queueAlertMessage && (
          <Alert
            severity="info"
            message={queueAlertMessage}
            onClose={() => setQueueAlertMessage(null)}
          />
        )}
        {/* Performance Summary - Only show when viewing combined analytics */}
        {performance && showCombinedAnalytics && (
          <CampaignAnalyticsCards
            analytics={performance}
            showCombinedBadge={showCombinedAnalytics}
            selectedCampaignsCount={
              dropdownSelectedCampaigns.length > 0
                ? dropdownSelectedCampaigns.length
                : dateFrom || dateTo
                  ? campaigns.length
                  : selectedCampaigns.size
            }
            onReset={handleResetAnalytics}
            formatCurrency={formatCurrency}
            formatPercentage={formatPercentage}
            rangeTotalSpend={rangeTotalSpend}
          />
        )}

        {/* Controls with Date Filter */}
        <div className="campaign-controls">
          <div className="controls-top-row">
            <div className="controls-left">
              <button 
                onClick={refreshCampaigns}
                className="refresh-button"
                disabled={loading}
              >
                {loading ? '🔄 Loading...' : '🔄 Refresh'}
              </button>
              <button 
                onClick={() => setShowCampaignUploadModal(true)}
                className="upload-campaign-button"
                disabled={loading}
              >
                <Upload size={16} />
                Upload Campaign CSV
              </button>
              <div className="date-filter-inline">
                <div className="date-input-group-inline">
                  <label htmlFor="dateFrom">From</label>
                  <input
                    type="date"
                    id="dateFrom"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="date-input-inline"
                    disabled={loading}
                  />
                </div>
                <div className="date-input-group-inline">
                  <label htmlFor="dateTo">To</label>
                  <input
                    type="date"
                    id="dateTo"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="date-input-inline"
                    disabled={loading}
                    min={dateFrom || undefined}
                  />
                </div>
                <button
                  onClick={handleApplyDateFilter}
                  className="apply-date-filter-btn-inline"
                  disabled={loading || (!dateFrom && !dateTo)}
                >
                  Apply
                </button>
                <button
                  onClick={handleClearDateFilter}
                  className="clear-date-filter-btn-inline"
                  disabled={loading || (!dateFrom && !dateTo)}
                >
                  Clear
                </button>
              </div>
            </div>
            {dataLastUpdatedAt && (
              <div className="data-freshness-info">
                <span className="data-updated-dot" />
                <span>Data as of {dataLastUpdatedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                {nextUpdateAt && (
                  <span className="next-update-text">
                    · Next update at {nextUpdateAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="campaign-count">
            Showing {campaigns.length} of {totalCampaigns} campaigns
            {totalPages > 1 && (
              <span className="page-info"> • Page {currentPage} of {totalPages}</span>
            )}
            {selectedCampaigns.size > 0 && (
              <span className="selected-count"> • {selectedCampaigns.size} selected</span>
            )}
          </div>
        </div>

        {/* Campaign selector dropdown (all pages) for combined performance */}
        <div
          className={`campaign-select-dropdown-wrap ${dropdownSelectedCampaigns.length > 0 ? 'has-selection' : ''} ${showCampaignDropdown ? 'dropdown-open' : ''}`}
          ref={campaignDropdownRef}
        >
          <label className="campaign-select-label">Select campaigns (all pages)</label>
          <div className="campaign-select-trigger" onClick={handleCampaignDropdownToggle}>
            <input
              type="text"
              className="campaign-select-input"
              placeholder={
                loadingCampaignList
                  ? 'Loading...'
                  : dropdownSelectedCampaigns.length === 0
                    ? 'Choose or search campaigns for combined performance...'
                    : `${dropdownSelectedCampaigns.length} campaign${dropdownSelectedCampaigns.length !== 1 ? 's' : ''} selected • Type to search`
              }
              value={dropdownSearchQuery}
              onChange={(e) => setDropdownSearchQuery(e.target.value)}
              onFocus={handleCampaignFieldFocus}
              onClick={(e) => e.stopPropagation()}
              aria-label="Choose or search campaigns"
            />
            <span className="campaign-select-arrow">▼</span>
          </div>
          {showCampaignDropdown && (
            <div className="campaign-select-dropdown">
              <div className="campaign-select-actions">
                <button type="button" onClick={(e) => { e.stopPropagation(); handleDropdownSelectAll(); }} className="campaign-select-action-btn">
                  {filteredCampaignList.length > 0 && filteredCampaignList.every((c) => isDropdownCampaignSelected(c))
                    ? 'Deselect all'
                    : 'Select all'}
                  {filteredCampaignList.length !== campaignList.length && (
                    <span className="campaign-select-action-hint"> (in search)</span>
                  )}
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); handleDropdownClear(); }} className="campaign-select-action-btn">
                  Clear
                </button>
              </div>
              <div className="campaign-select-list">
                {loadingCampaignList ? (
                  <div className="campaign-select-loading">Loading campaigns...</div>
                ) : campaignList.length === 0 ? (
                  <div className="campaign-select-empty">No campaigns found. Apply date filter or refresh.</div>
                ) : filteredCampaignList.length === 0 ? (
                  <div className="campaign-select-empty">No campaigns match your search.</div>
                ) : (
                  filteredCampaignList.map((c) => (
                    <label key={dropdownCampaignKey(c)} className="campaign-select-option">
                      <input
                        type="checkbox"
                        checked={isDropdownCampaignSelected(c)}
                        onChange={() => handleDropdownCampaignToggle(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="campaign-select-option-label">
                        {c.campaignName} <span className="campaign-select-source">({c.source})</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
              <div className="campaign-select-footer">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleViewCombinedAnalyticsFromDropdown(); }}
                  className="campaign-select-view-btn"
                  disabled={dropdownSelectedCampaigns.length === 0 || loadingCombinedPerformance}
                >
                  {loadingCombinedPerformance ? 'Loading...' : 'View combined performance'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Multi-Campaign Actions */}
        {selectedCampaigns.size > 0 && (
          <div className="multi-campaign-actions">
            <div className="multi-actions-left">
              <button
                onClick={handleSelectAll}
                className="select-all-button"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="deselect-all-button"
              >
                Deselect All
              </button>
              <span className="selected-info">
                {selectedCampaigns.size} campaign{selectedCampaigns.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="multi-actions-right">
              <button
                onClick={handleDownloadCombinedConverted}
                className="combined-download-button converted"
                disabled={downloadingCombined}
              >
                <Download size={16} />
                Download Converted ({selectedCampaigns.size})
              </button>
              <button
                onClick={handleDownloadCombinedNonConverted}
                className="combined-download-button non-converted"
                disabled={downloadingCombined}
              >
                <Download size={16} />
                Download Non-Converted ({selectedCampaigns.size})
              </button>
              <button
                onClick={handleViewCombinedAnalytics}
                className="view-analytics-button"
              >
                📊 View Combined Analytics
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            message={error}
            action={
              <button onClick={refreshCampaigns} className="retry-button" type="button">
                Try Again
              </button>
            }
          >
            {error.includes('timeout') && (
              <div className="error-suggestions" style={{ marginTop: 12 }}>
                <p><strong>Performance Tips:</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>Use date filters to load specific time periods</li>
                  <li>Try refreshing during off-peak hours</li>
                  <li>Contact support if the issue persists</li>
                </ul>
              </div>
            )}
          </Alert>
        )}

        {/* Campaigns Table */}
        {tableCampaigns.length > 0 && !loading ? (
          <div className="campaign-table-wrapper">
            <table className="campaign-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedCampaigns.size === tableCampaigns.length &&
                        tableCampaigns.length > 0
                      }
                      onChange={handleSelectAll}
                      className="select-all-checkbox"
                    />
                  </th>
                  <th>Campaign Info</th>
                  <th>Performance</th>
                  <th>Downloads</th>
                  <th>Remove</th>
                </tr>
              </thead>
              <tbody>
                {tableCampaigns.map((campaign, index) => {
                  const convertedCount = campaign.customersWithOrders || 0;
                  const conversionRate = campaign.conversionRate || 0;
                  
                  const campaignKey = `${campaign.campaignName}-${campaign.source}`;
                  const convertedKey = `${campaign.campaignName}-${campaign.source}-converted`;
                  const nonConvertedKey = `${campaign.campaignName}-${campaign.source}-non-converted`;
                  const isSelected = selectedCampaigns.has(campaignKey);
                  
                  return (
                    <motion.tr
                      key={`${campaign.campaignName}-${campaign.source}-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={isSelected ? 'row-selected' : ''}
                    >
                      <td className="cell-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCampaignSelect(campaign)}
                          className="campaign-checkbox"
                        />
                      </td>
                      
                      {/* Campaign Info Column */}
                      <td className="cell-campaign-info">
                        <div className="campaign-info-content">
                          <div className="campaign-name-row">
                            <Tag size={16} className="info-icon" />
                            <span className="campaign-name">{campaign.campaignName}</span>
                            {canEditCampaign(campaign) && (
                              <button
                                className="edit-campaign-btn"
                                onClick={() => handleEditCampaign(campaign)}
                                title="Edit campaign (within 7 days)"
                              >
                                <Edit size={14} />
                              </button>
                            )}
                          </div>
                          <div className="campaign-detail-row">
                            <Calendar size={14} className="info-icon" />
                            <span>{formatDate(campaign.campaignDate)}</span>
                            <span className="separator">•</span>
                            <span className={`source-badge source-${campaign.source.toLowerCase()}`}>
                              {campaign.source}
                            </span>
                          </div>
                          <div className="campaign-detail-row">
                            <Target size={14} className="info-icon" />
                            <span>{campaign.objective}</span>
                          </div>
                          <div className="campaign-detail-row">
                            <Users size={14} className="info-icon" />
                            <span>Customers: {campaign.totalCustomers?.toLocaleString() || 0}</span>
                          </div>
                          <div className="campaign-detail-row">
                            <DollarSign size={14} className="info-icon" />
                            <span>Cost: {formatCurrency(campaign.campaignSpend || 0)}</span>
                          </div>
                        </div>
                      </td>
                      
                      {/* Performance Column */}
                      <td className="cell-performance">
                        <div className="performance-content">
                          {/* Converted and Conversion Rate side by side */}
                          <div className="performance-row">
                          <div className="performance-item">
                            <span className="performance-label">Converted:</span>
                              <span className="performance-value">{convertedCount}</span>
                          </div>
                            <div className="performance-item">
                              <span className="performance-label">Conversion Rate:</span>
                              <span className="performance-value">{formatPercentage(conversionRate)}</span>
                            </div>
                          </div>
                          
                          {/* Orders and Cost per Order side by side */}
                          <div className="performance-row">
                          <div className="performance-item">
                            <span className="performance-label">Orders:</span>
                            <span className="performance-value">{campaign.totalOrders || 0}</span>
                          </div>
                            <div className="performance-item">
                              <span className="performance-label">Cost per Order:</span>
                              <span className="performance-value">
                                {campaign.totalOrders > 0 && campaign.campaignSpend
                                  ? formatCurrency(campaign.campaignSpend / campaign.totalOrders)
                                  : '—'
                                }
                              </span>
                            </div>
                          </div>
                          
                          {/* GMV and ROI side by side */}
                          <div className="performance-row">
                          <div className="performance-item">
                            <span className="performance-label">GMV:</span>
                            <span className="performance-value">{formatCurrency(campaign.totalGMV || 0)}</span>
                          </div>
                          <div className="performance-item">
                            <span className="performance-label">ROI:</span>
                            <span className="performance-value">{campaign.roi ? `${campaign.roi.toFixed(2)}x` : '0x'}</span>
                          </div>
                          </div>
                          
                          {/* Activity in 24 hrs and Cost per Activity side by side */}
                          <div className="performance-row">
                          <div className="performance-item">
                            <span className="performance-label">Activity in 24 hrs:</span>
                            <span className="performance-value">{campaign.totalLogins || 0}</span>
                          </div>
                          <div className="performance-item">
                            <span className="performance-label">Cost per Activity:</span>
                            <span className="performance-value">
                              {campaign.totalLogins > 0 && campaign.campaignSpend
                                ? formatCurrency(campaign.campaignSpend / campaign.totalLogins)
                                : '—'
                              }
                            </span>
                          </div>
                          </div>
                          
                          {/* AOV - in row format to keep value close to label */}
                          <div className="performance-row">
                          <div className="performance-item">
                              <span className="performance-label">AOV:</span>
                              <span className="performance-value">
                                {campaign.avgOrderValue > 0 ? formatCurrency(campaign.avgOrderValue) : '—'}
                              </span>
                            </div>
                            <div className="performance-item">
                              {/* Empty placeholder to maintain row structure and keep AOV value close to label */}
                            </div>
                          </div>
                          
                          {/* New customers acquired and Customer acquisition cost */}
                          <div className="performance-row">
                            <div className="performance-item">
                              <span className="performance-label">New customers acquired:</span>
                              <span className="performance-value">{campaign.newCustomersAcquired ?? 0}</span>
                            </div>
                            <div className="performance-item">
                              <span className="performance-label">Customer acquisition cost:</span>
                              <span className="performance-value">
                                {campaign.customerAcquisitionCost != null && campaign.customerAcquisitionCost > 0
                                  ? formatCurrency(campaign.customerAcquisitionCost)
                                  : '—'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Downloads Column */}
                      <td className="cell-downloads">
                        <div className="download-buttons">
                          <button
                            className="download-button converted"
                            onClick={() => handleDownloadConverted(campaign)}
                            title={`Download ${convertedCount} converted customers`}
                            disabled={loadingCountKey === convertedKey || downloadingCampaignKey === convertedKey}
                          >
                            {loadingCountKey === convertedKey ? (
                              <>…</>
                            ) : downloadingCampaignKey === convertedKey ? (
                              <span className="counter-box-loader" aria-hidden="true" />
                            ) : (
                              <>
                                <UserCheck size={16} />
                                <span>{convertedCount}</span>
                              </>
                            )}
                          </button>
                          <button
                            className="download-button non-converted"
                            onClick={() => handleDownloadNonConverted(campaign)}
                            title={`Download non-converted customers`}
                            disabled={loadingCountKey === nonConvertedKey || downloadingCampaignKey === nonConvertedKey}
                          >
                            {loadingCountKey === nonConvertedKey ? (
                              <>…</>
                            ) : downloadingCampaignKey === nonConvertedKey ? (
                              <span className="counter-box-loader" aria-hidden="true" />
                            ) : (
                              <>
                                <UserX size={16} />
                                <span>{campaign.totalCustomers - convertedCount}</span>
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                      
                      {/* Remove Column */}
                      <td className="cell-actions">
                        <button
                          className="remove-button"
                          onClick={() => handleRemoveCampaign(campaign)}
                          title="Remove campaign from all customers"
                          disabled={removingKey === `${campaign.campaignName}-${campaign.source}`}
                        >
                          {removingKey === `${campaign.campaignName}-${campaign.source}` ? '…' : <Trash2 size={16} />}
                        </button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : !loading && !error ? (
          <div className="no-campaigns">
            <div className="no-campaigns-icon">📊</div>
            <div className="no-campaigns-title">No Campaigns Found</div>
            <div className="no-campaigns-subtitle">
              Start by creating your first campaign to see performance data here.
            </div>
          </div>
        ) : null}

        {/* Skeleton Loader for Page Changes */}
        {loading && (campaigns.length > 0 || (combinedTableCampaigns && combinedTableCampaigns.length > 0)) && (
          <div className="campaign-table-wrapper skeleton-wrapper">
            <table className="campaign-table">
              <thead>
                <tr>
                  <th>
                    <div className="skeleton-checkbox"></div>
                  </th>
                  <th>Campaign Info</th>
                  <th>Performance</th>
                  <th>Downloads</th>
                  <th>Remove</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, index) => (
                  <tr key={`skeleton-${index}`} className="skeleton-row">
                    <td className="cell-checkbox">
                      <div className="skeleton-checkbox"></div>
                    </td>
                    <td className="cell-campaign-info">
                      <div className="skeleton-content">
                        <div className="skeleton-line skeleton-title"></div>
                        <div className="skeleton-line skeleton-medium"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                      </div>
                    </td>
                    <td className="cell-performance">
                      <div className="skeleton-content">
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                      </div>
                    </td>
                    <td className="cell-downloads">
                      <div className="skeleton-buttons">
                        <div className="skeleton-button"></div>
                        <div className="skeleton-button"></div>
                      </div>
                    </td>
                    <td className="cell-actions">
                      <div className="skeleton-button-small"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && !combinedTableCampaigns && (
          <div className="pagination-container">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="pagination-button prev"
            >
              ← Previous
            </button>
            <div className="pagination-pages">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                // Show first page, last page, current page, and pages around current
                const showPage = 
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                
                if (!showPage) {
                  // Show ellipsis
                  if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="pagination-ellipsis">...</span>;
                  }
                  return null;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`pagination-button page ${pageNum === currentPage ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className="pagination-button next"
            >
              Next →
            </button>
          </div>
        )}

        {/* Loading Progress Indicator */}
        {loading && (
          <div className="loading-progress-container">
            <div className="loading-progress-bar">
              <div 
                className="loading-progress-fill" 
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
            <div className="loading-text">
              Loading campaigns... {Math.round(loadingProgress)}%
              {loadingProgress > 50 && (
                <span className="loading-hint"> (Processing performance data...)</span>
              )}
            </div>
          </div>
        )}

        {/* Performance Info */}
        {performanceInfo && !loading && (
          <div className="performance-info">
            <small>
              Loaded in {performanceInfo.processingTimeMs}ms
              {performanceInfo.fromCache && ' (cached)'}
              {performanceInfo.optimization && ` • ${performanceInfo.optimization}`}
            </small>
          </div>
        )}

        {/* Loading Indicator - Initial Load */}
        {loading && campaigns.length === 0 && (
          <div className="campaign-table-wrapper skeleton-wrapper">
            <table className="campaign-table">
              <thead>
                <tr>
                  <th>
                    <div className="skeleton-checkbox"></div>
                  </th>
                  <th>Campaign Info</th>
                  <th>Performance</th>
                  <th>Downloads</th>
                  <th>Remove</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(10)].map((_, index) => (
                  <tr key={`skeleton-${index}`} className="skeleton-row">
                    <td className="cell-checkbox">
                      <div className="skeleton-checkbox"></div>
                    </td>
                    <td className="cell-campaign-info">
                      <div className="skeleton-content">
                        <div className="skeleton-line skeleton-title"></div>
                        <div className="skeleton-line skeleton-medium"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                      </div>
                    </td>
                    <td className="cell-performance">
                      <div className="skeleton-content">
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                        <div className="skeleton-line skeleton-small"></div>
                      </div>
                    </td>
                    <td className="cell-downloads">
                      <div className="skeleton-buttons">
                        <div className="skeleton-button"></div>
                        <div className="skeleton-button"></div>
                      </div>
                    </td>
                    <td className="cell-actions">
                      <div className="skeleton-button-small"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Details Modal */}
        <AnimatePresence>
          {showDetailsModal && selectedCampaign && (
            <>
              <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeModal}
              />
              <motion.div
                className="details-modal"
                initial={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
                animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                exit={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
              >
                <div className="modal-header">
                  <h2 className="modal-title">{selectedCampaign.campaignName}</h2>
                  <button className="modal-close" onClick={closeModal}>×</button>
                </div>
                <div className="modal-content">
                  <table className="modal-details-table">
                    <tbody>
                      <tr>
                        <td className="modal-label">Orders</td>
                        <td className="modal-value">{selectedCampaign.totalOrders || 0}</td>
                        <td className="modal-label">GMV</td>
                        <td className="modal-value">{formatCurrency(selectedCampaign.totalGMV)}</td>
                      </tr>
                      <tr>
                        <td className="modal-label">Conversion Rate</td>
                        <td className="modal-value">{formatPercentage(selectedCampaign.conversionRate)}</td>
                        <td className="modal-label">ROI</td>
                        <td className="modal-value">{selectedCampaign.roi ? `${selectedCampaign.roi.toFixed(1)}x` : '0x'}</td>
                      </tr>
                      <tr>
                        <td className="modal-label">Campaign Spend</td>
                        <td className="modal-value">{formatCurrency(selectedCampaign.campaignSpend)}</td>
                        <td className="modal-label">Average Order Value</td>
                        <td className="modal-value">{selectedCampaign.avgOrderValue > 0 ? formatCurrency(selectedCampaign.avgOrderValue) : '₹0'}</td>
                      </tr>
                      <tr>
                        <td className="modal-label">Activity in 24 hrs</td>
                        <td className="modal-value">{selectedCampaign.totalLogins || 0}</td>
                        <td className="modal-label">Cost per Activity</td>
                        <td className="modal-value">
                          {selectedCampaign.totalLogins > 0 && selectedCampaign.campaignSpend
                            ? formatCurrency(selectedCampaign.campaignSpend / selectedCampaign.totalLogins)
                            : '—'
                          }
                        </td>
                      </tr>
                      <tr>
                        <td className="modal-label">Cost per Order</td>
                        <td className="modal-value">
                          {selectedCampaign.totalOrders > 0 
                            ? formatCurrency(selectedCampaign.costPerOrder || 0)
                            : '—'
                          }
                        </td>
                        <td className="modal-label">New customers acquired</td>
                        <td className="modal-value">{selectedCampaign.newCustomersAcquired ?? 0}</td>
                      </tr>
                      <tr>
                        <td className="modal-label">Customer acquisition cost</td>
                        <td className="modal-value">
                          {selectedCampaign.customerAcquisitionCost != null && selectedCampaign.customerAcquisitionCost > 0
                            ? formatCurrency(selectedCampaign.customerAcquisitionCost)
                            : '—'
                          }
                        </td>
                        <td className="modal-label">Coupon & Offers</td>
                        <td className="modal-value" colSpan="3">
                          <div className="modal-coupon-offers">
                            {selectedCampaign.coupon ? (
                              <span className="coupon-badge">{selectedCampaign.coupon}</span>
                            ) : (
                              <span className="no-coupon">—</span>
                            )}
                            {selectedCampaign.offers && selectedCampaign.offers.length > 0 && (
                              <div className="offers-list-modal">
                                {selectedCampaign.offers.map((offer, i) => (
                                  <span key={i} className="offer-tag">{offer}</span>
                                ))}
                    </div>
                            )}
                    </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Campaign Download Modal */}
        <CampaignDownloadModal
          showModal={showDownloadModal}
          onClose={closeDownloadModal}
          onDownloadWithoutCampaign={handleDownloadWithoutCampaign}
          onDownloadWithCampaign={handleOpenCampaignPopup}
          downloading={downloading}
          customerCount={downloadType === 'converted' ? convertedCount : nonConvertedCount}
          campaignType={downloadType}
          campaignName={downloadCampaign?.campaignName}
        />

        {/* Campaign Popup Modal */}
        <CampaignPopup
          showCampaignPopup={showCampaignPopup}
          campaignForm={campaignForm}
          handleCampaignFormChange={handleCampaignFormChange}
          handleOfferChange={handleOfferChange}
          handleSaveCampaignAndDownload={handleSaveCampaignAndDownload}
          closeCampaignPopup={() => {
            setShowCampaignPopup(false);
            setIsManuallyEditedCampaignName(false); // Reset manual edit flag
            setCampaignForm(getEmptyCampaignForm());
          }}
          savingCampaign={savingCampaign}
        />

        {/* Download format choice (after Save & Download in Campaign Popup) */}
        <DownloadFormatModal
          showModal={showDownloadFormatModal}
          onClose={() => setShowDownloadFormatModal(false)}
          onSelectFormat={handleDownloadFormatSelected}
          downloading={downloading}
        />

        {/* Edit Campaign Modal */}
        <CampaignPopup
          showCampaignPopup={showEditCampaignModal}
          campaignForm={campaignForm}
          handleCampaignFormChange={handleCampaignFormChange}
          handleOfferChange={handleOfferChange}
          handleSaveCampaignAndDownload={handleUpdateCampaign}
          closeCampaignPopup={closeEditCampaignModal}
          savingCampaign={updatingCampaign}
          isEditMode={true}
        />

        {/* Campaign Remove Modal */}
        <CampaignRemoveModal
          showModal={showRemoveModal}
          onClose={closeRemoveModal}
          onUpload={handleFileUpload}
          onRemoveEntire={handleRemoveEntireCampaign}
          uploading={removingCampaign}
          removingEntire={removingEntire}
          campaignName={campaignToRemove?.campaignName}
          source={campaignToRemove?.source}
        />

        {/* Campaign CSV Upload Modal */}
        <CampaignUploadModal
          showModal={showCampaignUploadModal}
          onClose={() => setShowCampaignUploadModal(false)}
          onUploadSuccess={refreshCampaigns}
        />
      </div>
    </motion.div>
  );
};

export default CampaignDashboard;